import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Office, Uuid, UserSimple } from './types';
import type { TechnologyAreaOption } from './jobReducer';

export interface PromotionRequestUser extends UserSimple {
  role?: string;
}

export interface PromotionRequestOpening {
  uuid: Uuid;
  level: string;
  custom_level?: string;
  status: string;
  title?: string;
  expected_date?: string;
  offices?: Office[];
}

export interface PromotionRequest {
  uuid: Uuid;
  job: { uuid: Uuid; name: string };
  status: string;
  created_at: string;
  created_by: PromotionRequestUser;
  openings: PromotionRequestOpening[];
  prior_opening: PromotionRequestOpening | null;
  opening_order: Uuid[];
  promotion_source: string;
  platform?: string[];
  job_boards: string[];
  promotion_period_type: string;
  duration: number;
  promotion_period_display: string;
  request_title: string;
  description: string;
  search_regions: Office[];
  technology_areas: TechnologyAreaOption[];
  recruiter_leads: UserSimple[];
  team_lead_notification_recipients: UserSimple[];
  translation_languages: string[];
  additional_requirements: string;
  has_publication_details: boolean;
  /* Publication details — null until Published. */
  publication_start_date?: string | null;
  publication_end_date?: string | null;
  publication_links?: string[];
  publication_comment?: string;
  publication_date?: string | null;
  published_by?: PromotionRequestUser | null;
  /* Change history (Non-editable attributes) — each pair is rendered only when its date is set. */
  approval_date?: string | null;
  approved_by?: PromotionRequestUser | null;
  decline_date?: string | null;
  declined_by?: PromotionRequestUser | null;
  decline_reason?: string;
  last_updated_at?: string | null;
  last_updated_by?: PromotionRequestUser | null;
  cancellation_date?: string | null;
  canceled_by?: PromotionRequestUser | null;
  cancellation_comment?: string;
  completion_date?: string | null;
  closure_date?: string | null;
  closed_by?: PromotionRequestUser | null;
}

export interface PromotionRequestState {
  promotionRequestData: PromotionRequest | Record<string, never>;
  loading: boolean;
}

export const initialState: PromotionRequestState = {
  promotionRequestData: {},
  loading: false,
};

const promotionRequestSlice = createSlice({
  name: 'promotionRequest',
  initialState,
  reducers: {
    setPromotionRequestData(state, { payload }: PayloadAction<PromotionRequest | Record<string, never>>) {
      state.promotionRequestData = payload;
    },
    setPromotionRequestLoading(state, { payload }: PayloadAction<boolean>) {
      state.loading = payload;
    },
    clearPromotionRequestData(state) {
      state.promotionRequestData = {};
    },
  },
});

export const { setPromotionRequestData, setPromotionRequestLoading, clearPromotionRequestData } =
  promotionRequestSlice.actions;
export default promotionRequestSlice.reducer;
